import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container, Grid, Card, Header, Loader, Dimmer, Label, Button, Message, Input,
} from 'semantic-ui-react';
import moment from 'moment';
import { getConferences } from './lib/tickets';
import { buildPath } from './lib/tenant';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function ConferenceList() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ticketing-conferences'],
        queryFn: getConferences,
    });

    const conferences = useMemo(() => data?.conferences ?? [], [data]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return conferences.filter(c =>
            !q || c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
        );
    }, [conferences, search]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('conferenceList.loading')} />
            </Dimmer>
        );
    }

    if (isError) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('conferenceList.errorTitle')}</Message.Header>
                    <p>{t('conferenceList.errorBody')}</p>
                </Message>
            </Container>
        );
    }

    return (
        <Container className="contentContainer">
            <Header as="h1" className="sectionHeader" style={{ marginBottom: '1.5rem' }}>
                {t('conferenceList.title')}
            </Header>

            <div style={{ marginBottom: '1.5rem' }}>
                <Input
                    icon="search"
                    iconPosition="left"
                    placeholder={t('conferenceList.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ minWidth: 260 }}
                />
            </div>

            {filtered.length === 0 && (
                <Message info>
                    <Message.Header>{t('conferenceList.noConferencesTitle')}</Message.Header>
                    <p>{search ? t('conferenceList.noConferencesFiltered') : t('conferenceList.noConferencesEmpty')}</p>
                </Message>
            )}

            <Grid stackable columns={3}>
                {filtered.map(conference => {
                    const isSoldOut = conference.tickets_remaining === 0;

                    return (
                        <Grid.Column key={conference.id}>
                            <Card
                                fluid
                                className="eventCard"
                                onClick={() => navigate(buildPath(`/conferences/${conference.id}`))}
                                style={{ borderRadius: 12, overflow: 'hidden', cursor: 'pointer', opacity: isSoldOut ? 0.7 : 1 }}
                            >
                                <div style={{
                                    height: 140,
                                    background: 'linear-gradient(135deg, var(--colour_menu_main), var(--colour_menu_light))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '3rem' }}>🎪</span>
                                </div>
                                <Card.Content>
                                    <Card.Header style={{ color: isSoldOut ? '#999' : 'var(--colour_menu_main)' }}>
                                        {conference.name}
                                    </Card.Header>
                                    {conference.start_date && (
                                        <Card.Meta>
                                            {moment(conference.start_date).format('D MMM YYYY')}
                                            {conference.end_date && conference.end_date !== conference.start_date
                                                ? ` – ${moment(conference.end_date).format('D MMM YYYY')}` : ''}
                                        </Card.Meta>
                                    )}
                                    {conference.session_count > 0 && (
                                        <Card.Meta>{t('conferenceList.sessionsCount', { count: conference.session_count })}</Card.Meta>
                                    )}
                                    <Card.Description style={{
                                        marginTop: '0.5rem', overflow: 'hidden',
                                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                    }}>
                                        {conference.description}
                                    </Card.Description>
                                </Card.Content>
                                <Card.Content extra style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Label style={{
                                        backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_heading_success)',
                                        color: '#fff', fontWeight: 700,
                                    }}>
                                        {isSoldOut ? t('conferenceList.soldOut') : formatMoney(conference.display_price ?? conference.ticket_price)}
                                    </Label>
                                    <Button size="small" style={{
                                        backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_menu_main)',
                                        color: '#fff',
                                    }}>
                                        {isSoldOut ? t('conferenceList.view') : t('conferenceList.getPass')}
                                    </Button>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    );
                })}
            </Grid>
        </Container>
    );
}

export default ConferenceList;
